import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getFullHierarchy } from '../utils/api';
import { ChevronRight, ChevronDown, Users, ListTree } from 'lucide-react';
import { roleLabel } from '../utils/roles';

const ROLE_COLORS = {
  clm: { bg: '#EDE7F6', color: '#5E35B1' },
  bdo: { bg: 'var(--blue-light)', color: '#185FA5' },
  sales_rep: { bg: '#FFF3E0', color: '#B36A00' },
  crm: { bg: '#FCE4EC', color: '#AD1457' },
  associate: { bg: 'var(--green-light)', color: 'var(--green-dark)' },
  leader: { bg: 'var(--blue-light)', color: '#185FA5' }, // legacy
};

function TreeNode({ node, depth }) {
  const [open, setOpen] = useState(depth < 2);
  const hasChildren = node.children && node.children.length > 0;
  const roleStyle = ROLE_COLORS[node.role] || ROLE_COLORS.associate;

  return (
    <div style={{ marginLeft: depth === 0 ? 0 : 22, position: 'relative' }}>
      {depth > 0 && (
        <div style={{ position: 'absolute', left: -14, top: 0, bottom: 0, width: 1, borderLeft: '1.5px dashed var(--border)' }} />
      )}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
        borderRadius: 10, border: '1.5px solid var(--border)', background: '#fff', marginBottom: 6,
      }}>
        {hasChildren ? (
          <button onClick={() => setOpen(!open)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', display: 'flex', padding: 0 }}>
            {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
          </button>
        ) : <span style={{ width: 15 }} />}
        <div style={{
          width: 30, height: 30, borderRadius: '50%', background: roleStyle.bg, color: roleStyle.color,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0
        }}>
          {node.name?.charAt(0)?.toUpperCase() || '?'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13.5 }}>{node.name}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{node.email}</div>
        </div>
        <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 9px', borderRadius: 20, background: roleStyle.bg, color: roleStyle.color }}>
          {roleLabel(node.role)}
        </span>
        {!node.is_active && (
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: 'var(--red-light)', color: 'var(--red)' }}>Inactive</span>
        )}
        {hasChildren && (
          <span style={{ fontSize: 11, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 3 }}>
            <Users size={12} /> {node.children.length}
          </span>
        )}
      </div>
      {hasChildren && open && (
        <div>{node.children.map(child => <TreeNode key={child.id} node={child} depth={depth + 1} />)}</div>
      )}
    </div>
  );
}

export default function AdminHierarchy() {
  const { data: trees = [], isLoading } = useQuery({ queryKey: ['full-hierarchy'], queryFn: getFullHierarchy });

  const grandTotal = trees.reduce((sum, t) => sum + 1 + (t.total_team_size || 0), 0);

  return (
    <div style={{ padding: 28 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
          <ListTree size={22} color="var(--green)" /> Sales Hierarchy
        </h1>
        <p style={{ color: 'var(--text3)', fontSize: 13, marginTop: 2 }}>
          Reporting structure across CLMs, BDOs, Sales Representatives, and Associates — who added whom, and team sizes.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14, marginBottom: 22, maxWidth: 480 }}>
        <div className="stat-card">
          <div className="stat-val" style={{ color: 'var(--blue)' }}>{trees.length}</div>
          <div className="stat-lbl">Top-Level Team Members</div>
        </div>
        <div className="stat-card">
          <div className="stat-val" style={{ color: 'var(--green)' }}>{grandTotal}</div>
          <div className="stat-lbl">Total People in Hierarchy</div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="spinner" /></div>
          ) : trees.length === 0 ? (
            <div className="empty-state">
              <Users size={32} />
              <p>No sales team members yet. Add one from the Sales Team page to start building the hierarchy.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {trees.map(tree => (
                <div key={tree.id}>
                  <TreeNode node={tree} depth={0} />
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 4, marginTop: -2, marginBottom: 8 }}>
                    Total team under {tree.name}: <strong>{tree.total_team_size}</strong>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
